import { createSlice } from "@reduxjs/toolkit";
import {
  EnumGridDataShowRowCount,
  EnumListShowRowCountList,
  EnumRemediationTrackerTaskColumnType,
  EnumRemediationTrackerTaskStatus,
  EnumSortTypes,
} from "../../../common/enums/common.enums";
import {
  AssessmentTypeDTO,
  DropDownDTO,
  PaginationDTO,
  RemediationTrackerTaskDTO,
  RemediationTrackerTaskGridData,
  RemediationTrackerTaskQuestionDTO,
  RemediationTrackerTaskSearchParameter,
  RemediationTrackerTaskStatusDTO,
  SearchParameterDTO,
  SortDropDownDTO,
  StatusSummaryCountDTO,
} from "../../../common/types/common.dto.types";

// project imports
import {
  RemediationTrackerTaskGridDataType,
  RemediationTrackerTaskQuestionsResponseDTO,
  RemediationTrackerTasksPageType,
  TaskFilteredSummaryType,
  TaskSelectionType,
  ViewDetailsPopupDataType,
} from "./RemediationTrackerTasksPage.types";
import dayjs from "dayjs";
import { ListColumn } from "../../components/ViewRemediationTrackerTaskList/ViewRemediationTrackerTaskList.types";

// ==============================|| VIEWS - PAGES - REMEDIATION TRACKER TASKS - REDUCER ||============================== //

const initialState = {
  GridData: [] as Array<RemediationTrackerTaskGridDataType>,
  TaskSearchParameter: {
    TaskName: "",
    StatusId: 0,
    SelectedActionOwnerIdList: [] as Array<string>,
    SelectedLocationIdList: [] as Array<number>,
    SelectedStandardIdList: [] as Array<number>,
    SelectedPriorityIdList: [] as Array<number>,
    SelectedStatusIdList: [] as Array<number>,
    SelectedInspectionIdList: [] as Array<number>,
    SelectedApproverIdList: [] as Array<number>,
    SelectedPendingApproverIdList: [] as Array<string>,
    SelectedIsCapexRequiredIdList: [] as Array<number>,
    SelectedCapexStatusIdList: [] as Array<number>,
    SelectedAreaOwnerTypeIdList: [] as Array<number>,
    InspectionName: "",
    QuestionNumber: "",
    ActionOwner: "",
    FromDueDate: null,
    ToDueDate: null,
    CapexamountRequired: "",
    CapexnumberAndName: "",
    RemediationTrackerTaskSortBy: {
      Label: "TaskId",
      Value: EnumRemediationTrackerTaskColumnType.TaskId.toString(),
      SortType: EnumSortTypes.Ascending,
    } as SortDropDownDTO,
    ShowTasksAssignedToMe: false,
    ShowTasksPendingMyApproval: false,
    ShowCompletedTasks: false,
    ShowCancelledTasks: false,
    Ernumber: "",
    AssessmentTypeGroupId: "0",

    IsAdvanceSearch: false,
    SelectedPage: 1,
    GridPageSize: EnumGridDataShowRowCount.GridDataShowRowCount_3,
  } as RemediationTrackerTaskSearchParameter,
  SelectedTask: [] as Array<TaskSelectionType>,
  DropDownListFilter: {
    LocationDropDownList: [] as Array<DropDownDTO>,
    InspectionDropDownList: [] as Array<DropDownDTO>,
    StandardDropDownList: [] as Array<DropDownDTO>,
    ActionOwnerDropDownList: [] as Array<DropDownDTO>,
    PendingApproverDropDownList: [] as Array<DropDownDTO>,
    PriorityDropDownList: [] as Array<DropDownDTO>,
    StatusDropDownList: [] as Array<DropDownDTO>,
    IsCapexRequiredDropDownList: [] as Array<DropDownDTO>,
    CapexStatusDropDownList: [] as Array<DropDownDTO>,
    AreaOwnerTypeDropDownList: [] as Array<DropDownDTO>,
  } as RemediationTrackerTaskDTO,
  ShowFilters: false,
  TaskStatusSummaryCount: [] as Array<RemediationTrackerTaskStatusDTO>,
  TaskAssessmentSummary: [] as Array<AssessmentTypeDTO>,
  TaskFilteredSummary: [] as Array<TaskFilteredSummaryType>,
  DataLoading: false,
  MergeTaskBetweenMultipleLocationsAndInspection: false,
  IsFullScreen: false,
  IsEnableCreateMergeTask: false,
  ClearExpandedRows: false,
  ColumnOptions: [] as Array<ListColumn>,
  InitialDataLoaded: false,
  IsEnableUnMergeTask: false,
  ViewDetailsPopUpData: {
    OpenPopup: false,
    Data: [],
  } as ViewDetailsPopupDataType,
} as RemediationTrackerTasksPageType;

export const remediationTrackerTaskSlice = createSlice({
  name: "RemediationTrackerTaskList",
  initialState: initialState,
  reducers: {
    setRemediationTrackerTaskInitialState: (
      state: RemediationTrackerTasksPageType,
      action: { payload: RemediationTrackerTasksPageType; type: string }
    ) => {
      return {
        ...state,
        GridData: action.payload.GridData,
        TaskSearchParameter: action.payload.TaskSearchParameter,
        SelectedTask: action.payload.SelectedTask,
        DropDownListFilter: action.payload.DropDownListFilter,
        TaskStatusSummaryCount: action.payload.TaskStatusSummaryCount,
      };
    },
    setRemediationTrackerTaskList: (
      state: RemediationTrackerTasksPageType,
      action: { payload: Array<RemediationTrackerTaskGridData>; type: string }
    ) => {
      const taskGridData = [
        ...action.payload?.map((parentTask) => {
          const taskIsSelected = state.SelectedTask?.some(
            (some) => some.ParentTaskId === parentTask.ParentTaskId
          );

          return {
            ParentTask: {
              ...parentTask,
              TaskIsChecked: taskIsSelected,
            },
          } as RemediationTrackerTaskGridDataType;
        }),
      ];

      return {
        ...state,
        GridData: taskGridData,
        ClearExpandedRows: true,
      };
    },
    setChildTaskDetails: (
      state: RemediationTrackerTasksPageType,
      action: {
        payload: {
          parentTaskId: number;
          childTasks: Array<RemediationTrackerTaskGridData>;
        };
        type: string;
      }
    ) => {
      const { parentTaskId, childTasks } = action.payload;

      const updatedGridData =
        state.GridData?.map((task) => {
          const currentParentId = task?.ParentTask?.ParentTaskId;

          if (currentParentId !== parentTaskId) {
            return task;
          }

          // Get selected children for this parent from state
          const selectedParent = state.SelectedTask?.find(
            (t) => t.ParentTaskId === currentParentId
          );

          const selectedChildIds = selectedParent?.ChildTaskIds ?? [];

          // parent is selected but no children set all as selected
          const selectAllChildren =
            selectedParent && selectedChildIds.length === 0;

          const updatedChildTasks = childTasks.map((child) => ({
            ...child,
            TaskIsChecked:
              selectAllChildren || selectedChildIds.includes(child.ChildTaskId),
          }));

          return {
            ...task,
            ChildTask: updatedChildTasks,
          };
        }) ?? [];

      return {
        ...state,
        GridData: updatedGridData,
        ClearExpandedRows: false,
      };
    },
    setDropDownList: (
      state: RemediationTrackerTasksPageType,
      action: { payload: RemediationTrackerTaskDTO; type: string }
    ) => {
      return {
        ...state,
        DropDownListFilter: {
          ...state.DropDownListFilter,
          LocationDropDownList: action.payload?.LocationDropDownList,
          InspectionDropDownList: action.payload?.InspectionDropDownList,
          StandardDropDownList: action.payload?.StandardDropDownList,
          ActionOwnerDropDownList: action.payload?.ActionOwnerDropDownList,
          PriorityDropDownList: action.payload?.PriorityDropDownList,
          StatusDropDownList: action.payload?.StatusDropDownList,
          IsCapexRequiredDropDownList:
            action.payload?.IsCapexRequiredDropDownList,
          CapexStatusDropDownList: action.payload?.CapexStatusDropDownList,
          AreaOwnerTypeDropDownList: action.payload?.AreaOwnerTypeDropDownList,
          PendingApproverDropDownList:
            action.payload?.PendingApproverDropDownList,
        },
        InitialDataLoaded: true,
      };
    },
    setRemediationTaskSystemConfiguration: (
      state: RemediationTrackerTasksPageType,
      action: { payload: RemediationTrackerTaskDTO; type: string }
    ) => {
      return {
        ...state,
        MergeTaskBetweenMultipleLocationsAndInspection:
          action.payload.MergeTaskBetweenMultipleLocationsAndInspection,
      };
    },
    setRemediationTrackerTaskSearchParameter: (
      state: RemediationTrackerTasksPageType,
      action: { payload: RemediationTrackerTaskSearchParameter; type: string }
    ) => {
      let taskFilteredValue = [] as Array<TaskFilteredSummaryType>;

      if (action.payload?.IsAdvanceSearch) {
        //set Location Filtered List

        const locationList =
          state.DropDownListFilter?.LocationDropDownList?.filter((filter) =>
            action.payload?.SelectedLocationIdList?.includes(
              parseInt(filter.Value)
            )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedLocationIdList",
            Label: "Location",
            Value: locationList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Inspection Filtered List
        const inspectionList =
          state.DropDownListFilter?.InspectionDropDownList?.filter((filter) =>
            action.payload?.SelectedInspectionIdList?.includes(
              parseInt(filter.Value)
            )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedInspectionIdList",
            Label: "Inspection",
            Value: inspectionList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Standard Filtered List
        const standardList =
          state.DropDownListFilter?.StandardDropDownList?.filter((filter) =>
            action.payload?.SelectedStandardIdList?.includes(
              parseInt(filter.Value)
            )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedStandardIdList",
            Label: "Standard",
            Value: standardList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Q_No Filtered
        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "QuestionNumber",
            Label: "Q.No",
            Value: action.payload?.QuestionNumber,
            DefaultValue: "",
          } as TaskFilteredSummaryType,
        ];
        // }

        //set Task Name Filtered
        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "TaskName",
            Label: "Task Description",
            Value: action.payload?.TaskName,
            DefaultValue: "",
          } as TaskFilteredSummaryType,
        ];

        //set Action Owner Filtered List
        const actionOwnerList =
          state.DropDownListFilter?.ActionOwnerDropDownList?.filter((filter) =>
            action.payload?.SelectedActionOwnerIdList?.map((item) => {
              return item?.toString();
            })?.includes(filter.Value?.toString())
          )
            ?.map((item) => item.Label)
            ?.join(" | ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedActionOwnerIdList",
            Label: "Action Owner",
            Value: actionOwnerList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Task Status Filtered List
        const statusList = state.DropDownListFilter?.StatusDropDownList?.filter(
          (filter) =>
            action.payload?.SelectedStatusIdList?.includes(
              parseInt(filter.Value)
            )
        )
          ?.map((item) => item.Label)
          ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedStatusIdList",
            Label: "Status",
            Value: statusList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Priority Filtered List
        const priorityList =
          state.DropDownListFilter?.PriorityDropDownList?.filter((filter) =>
            action.payload?.SelectedPriorityIdList?.includes(
              parseInt(filter.Value)
            )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedPriorityIdList",
            Label: "Priority",
            Value: priorityList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Is Capex Required Filtered List
        const isCAPEXRequiredList =
          state.DropDownListFilter?.IsCapexRequiredDropDownList?.filter(
            (filter) =>
              action.payload?.SelectedIsCapexRequiredIdList?.includes(
                parseInt(filter.Value)
              )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedIsCapexRequiredIdList",
            Label: "Is CAPEX Required",
            Value: isCAPEXRequiredList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Capex number And Name Filtered
        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "CapexnumberAndName",
            Label: "CAPEX Number & Name",
            Value: action.payload?.CapexnumberAndName,
            DefaultValue: "",
          } as TaskFilteredSummaryType,
        ];

        //set Capex Status Filtered List
        const cAPEXStatusList =
          state.DropDownListFilter?.CapexStatusDropDownList?.filter((filter) =>
            action.payload?.SelectedCapexStatusIdList?.includes(
              parseInt(filter.Value)
            )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedCapexStatusIdList",
            Label: "CAPEX Status",
            Value: cAPEXStatusList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Capex amount Required Filtered
        const currencyFormatter = new Intl.NumberFormat("en-US", {
          //style: "number",
          currency: "USD",
        });

        const converedcapexamountRequired = parseFloat(
          action.payload?.CapexamountRequired ?? "0"
        );
        const capexamountRequired = currencyFormatter
          .format(
            isNaN(converedcapexamountRequired) ? 0 : converedcapexamountRequired
          )
          ?.toString();

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "CapexamountRequired",
            Label: "CAPEX Amount Required (M$)",
            Value: capexamountRequired === "0" ? "" : capexamountRequired,
            DefaultValue: null,
          } as TaskFilteredSummaryType,
        ];

        //set To Due Date Filtered
        const _fromDueDate = dayjs(action.payload?.FromDueDate).format(
          "DD-MMM-YYYY"
        );
        const fromDueDate = _fromDueDate !== "Invalid Date" ? _fromDueDate : "";

        const _toDueDate = dayjs(action.payload?.ToDueDate).format(
          "DD-MMM-YYYY"
        );
        const toDueDate = _toDueDate !== "Invalid Date" ? _toDueDate : "";

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "DueDate",
            Label: "Due Date",
            Value: `${fromDueDate ? "From " + fromDueDate : ""}  ${
              fromDueDate && toDueDate ? " To " : ""
            }${toDueDate}`,
            DefaultValue: null,
          } as TaskFilteredSummaryType,
        ];

        //set Category Filtered List
        const categoryList =
          state.DropDownListFilter?.AreaOwnerTypeDropDownList?.filter(
            (filter) =>
              action.payload?.SelectedAreaOwnerTypeIdList?.includes(
                parseInt(filter.Value)
              )
          )
            ?.map((item) => item.Label)
            ?.join(", ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedAreaOwnerTypeIdList",
            Label: "Category",
            Value: categoryList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set Pending Approver Filtered List
        const pendingApproverList =
          state.DropDownListFilter?.PendingApproverDropDownList?.filter(
            (filter) =>
              action.payload?.SelectedPendingApproverIdList?.map((item) => {
                return item?.toString();
              })?.includes(filter.Value?.toString())
          )
            ?.map((item) => item.Label)
            ?.join(" | ");

        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "SelectedPendingApproverIdList",
            Label: "Pending Approver(s)",
            Value: pendingApproverList,
            DefaultValue: [],
          } as TaskFilteredSummaryType,
        ];

        //set ER number Filtered
        taskFilteredValue = [
          ...taskFilteredValue,
          {
            Field: "Ernumber",
            Label: "ER Number",
            Value: action.payload?.Ernumber,
            DefaultValue: "",
          } as TaskFilteredSummaryType,
        ];
      }

      const isAdvanceSearch =
        taskFilteredValue &&
        taskFilteredValue?.filter(
          (filter) => filter.Value && filter.Value?.trim() !== ""
        )?.length > 0;

      return {
        ...state,
        TaskSearchParameter: {
          ...action.payload,
          IsAdvanceSearch: state.InitialDataLoaded
            ? isAdvanceSearch
            : action.payload.IsAdvanceSearch,
          SelectedPage: action.payload.SelectedPage,
          ListPageSize: action.payload.ListPageSize,
          BasicSearchValue: action.payload.BasicSearchValue,
          FilterColumnWise: action.payload.FilterColumnWise,
        } as RemediationTrackerTaskSearchParameter,
        TaskFilteredSummary: taskFilteredValue?.filter(
          (filter) => filter.Value && filter.Value?.trim() !== ""
        ),
        ClearExpandedRows: true,
      };
    },
    setDataLoading: (
      state: RemediationTrackerTasksPageType,
      action: { payload: boolean; type: string }
    ) => {
      return {
        ...state,
        DataLoading: action.payload,
      };
    },
    setTaskSelectionChange: (
      state: RemediationTrackerTasksPageType,
      action: { payload: Array<TaskSelectionType>; type: string }
    ) => {
      const updatedGridData =
        state.GridData?.map((task) => {
          const parentId = task?.ParentTask?.ParentTaskId;

          const selectedParentTask = action.payload.find(
            (t) => t.ParentTaskId === parentId
          );

          const selectedChildIds = selectedParentTask?.ChildTaskIds ?? [];

          const updatedChildTasks =
            task.ChildTask?.map((child) => ({
              ...child,
              TaskIsChecked: selectedChildIds.includes(child.ChildTaskId),
            })) ?? [];

          const isParentChecked =
            selectedParentTask &&
            (updatedChildTasks.length === 0 ||
              (updatedChildTasks.length > 0 &&
                updatedChildTasks.every((child) => child.TaskIsChecked)));

          return {
            ...task,
            ParentTask: {
              ...task.ParentTask,
              TaskIsChecked: isParentChecked,
            },
            ChildTask: updatedChildTasks,
          };
        }) ?? [];

      return {
        ...state,
        SelectedTask: action.payload,
        GridData: updatedGridData,
      };
    },
    resetSelectedRemediationTrackerTask: (
      state: RemediationTrackerTasksPageType,
      action: { payload: boolean; type: string }
    ) => {
      return {
        ...state,
        SelectedTask: [],
        SelectedRemediationTrackerTask: [],
        ClearExpandedRows: true,
      };
    },
    setRemediationTrackerTaskColumnOptions: (
      state: RemediationTrackerTasksPageType,
      action: { payload: Array<ListColumn>; type: string }
    ) => {
      return {
        ...state,
        ColumnOptions: action.payload,
      };
    },
    setTaskStatusSummary: (
      state: RemediationTrackerTasksPageType,
      action: { payload: Array<RemediationTrackerTaskStatusDTO>; type: string }
    ) => {
      return {
        ...state,
        TaskStatusSummaryCount: action.payload,
      };
    },
    setTaskAssessmentSummary: (
      state: RemediationTrackerTasksPageType,
      action: { payload: Array<AssessmentTypeDTO>; type: string }
    ) => {
      return {
        ...state,
        TaskAssessmentSummary: action.payload,
      };
    },
    resetRemediationTrackerTaskList: () => {
      return initialState;
    },
    setEnableCreateMergeTask: (
      state: RemediationTrackerTasksPageType,
      action: { payload: boolean; type: string }
    ) => {
      return {
        ...state,
        IsEnableCreateMergeTask: action.payload,
      };
    },
    setEnableUnMergeTask: (
      state: RemediationTrackerTasksPageType,
      action: { payload: boolean; type: string }
    ) => {
      return {
        ...state,
        IsEnableUnMergeTask: action.payload,
      };
    },
    setViewDetailspopUpData: (
      state: RemediationTrackerTasksPageType,
      action: {
        payload: ViewDetailsPopupDataType;
        type: string;
      }
    ) => {
      return {
        ...state,

        ViewDetailsPopUpData: action.payload,
      };
    },
  },
});

export const remediationTrackerTasksReducerActions =
  remediationTrackerTaskSlice.actions;
export const remediationTrackerTasksReducer =
  remediationTrackerTaskSlice.reducer;
